import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const ContactUsPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  const inputClass =
    'w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-navy-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition-all duration-200';

  return (
    <>
      <Helmet>
        <title>Contact Mato Industries – Delhi, India</title>
        <meta name="description" content="Get in touch with Mato Industries. Phone: +91-9910934449. Email: sales@matoindustries.com. Address: A-5, G.T. Karnal Road, Delhi 110033." />
        <link rel="canonical" href="https://www.matoindustries.com/contact" />
        <meta property="og:url" content="https://www.matoindustries.com/contact" />
        <meta property="og:title" content="Contact Mato Industries – Delhi, India" />
        <meta property="og:description" content="Get in touch with Mato Industries. Phone: +91-9910934449. Email: sales@matoindustries.com." />
      </Helmet>
      <div className="min-h-screen bg-slate-50">

      {/* Page header */}
      <div className="bg-navy-900 text-white py-14 px-4">
        <div className="container mx-auto max-w-7xl">
          <span className="text-brand-400 text-sm font-semibold uppercase tracking-widest">Reach Us</span>
          <h1 className="text-4xl md:text-5xl font-extrabold mt-2 mb-3">Contact Us</h1>
          <p className="text-slate-400 text-lg max-w-xl">
            Send us your requirements or drawings and we'll get back within 24 hours.
          </p>
        </div>
      </div>

      <main className="container mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Left: contact info + map */}
          <div className="space-y-6">

            {/* Contact cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="tel:+919910934449"
                className="group flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-brand-200 transition-all duration-200 cursor-pointer"
              >
                <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-brand-200 transition-colors">
                  <svg className="w-5 h-5 text-brand-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Phone</div>
                  <div className="text-sm font-bold text-navy-900 group-hover:text-brand-700 transition-colors">+91-9910934449</div>
                </div>
              </a>

              <a
                href="mailto:sales@matoindustries.com"
                className="group flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-brand-200 transition-all duration-200 cursor-pointer"
              >
                <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-brand-200 transition-colors">
                  <svg className="w-5 h-5 text-brand-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Email</div>
                  <div className="text-sm font-bold text-navy-900 group-hover:text-brand-700 transition-colors break-all">sales@matoindustries.com</div>
                </div>
              </a>
            </div>

            {/* Address */}
            <div className="flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-5">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Address</div>
                <address className="not-italic text-sm text-navy-900 leading-relaxed font-medium">
                  A-5, G.T. Karnal Road<br />
                  Industrial Area,<br />
                  Delhi – 110033, India
                </address>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-md border border-slate-200 aspect-[4/3]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3499.6623815885164!2d77.18023951238291!3d28.699744380921345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd8a13022c81%3A0xa0c717abb290efca!2sMato%20Industries!5e0!3m2!1sen!2sin!4v1758287739048!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mato Industries Location"
              />
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-sm">
            <h2 className="text-2xl font-extrabold text-navy-900 mb-1">Send us a Message</h2>
            <p className="text-slate-500 text-sm mb-7">Share your requirements and we'll respond within 24 hours.</p>

            {formSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-1">Message Received!</h3>
                <p className="text-slate-500 text-sm">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-navy-800 mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-navy-800 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-navy-800 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe your requirements, specifications, or questions..."
                    className={inputClass}
                    rows={5}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-colors duration-200 cursor-pointer shadow-sm shadow-brand-900/20"
                >
                  Send Message
                </button>
              </form>
            )}
          </div>

        </div>
      </main>
      </div>
    </>
  );
};

export default ContactUsPage;
